import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ProvenanceBadge from "@/components/ProvenanceBadge";
import { fmtDateTime, fmtEUR, fmtNum } from "@/lib/format";
import { FileImage, FileText, Download, X as XIcon, Trash2, AlertTriangle, Pencil, Save } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  manual: "Manuell", manual_enriched: "Manuell + Broker",
  okx: "OKX", bitget: "Bitget", binance: "Binance",
  kraken: "Kraken", bitunix: "Bitunix", levex: "LeveX",
};

export default function TradeDetailDialog({ trade, open, onOpenChange, onDeleted, onUpdated }) {
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setPreview(null); setConfirmDelete(false); setEditing(false); }
    if (trade) setForm({
      strategy_name: trade.strategy_name || "",
      stop: trade.stop ?? "",
      risk_pct: trade.risk_pct ?? "",
      mood_before: trade.mood_before || "",
      mood_after: trade.mood_after || "",
      reason: trade.reason || "",
      reflection: trade.reflection || "",
      tags: (trade.tags || []).join(", "),
      entry: trade.entry ?? "",
      exit: trade.exit ?? "",
      size: trade.size ?? "",
      fees: trade.fees ?? "",
      leverage: trade.leverage ?? "",
      opened_at: trade.opened_at ? trade.opened_at.slice(0, 16) : "",
      closed_at: trade.closed_at ? trade.closed_at.slice(0, 16) : "",
    });
  }, [open, trade]);

  if (!trade) return null;
  const attachments = trade.attachments || [];
  const pl = trade.pl;
  const plColor = pl == null ? "" : pl >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]";
  const isManual = trade.provenance === "manual" || trade.provenance === "manual_enriched";
  const isBroker = !isManual;
  const hasNoDates = !trade.opened_at || !trade.closed_at;
  const deletable = isManual;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toNum = (v) => { if (v === "" || v == null) return null; const n = parseFloat(String(v).replace(",", ".")); return Number.isFinite(n) ? n : null; };

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = {
        strategy_name: form.strategy_name,
        stop: toNum(form.stop),
        risk_pct: toNum(form.risk_pct),
        mood_before: form.mood_before,
        mood_after: form.mood_after,
        reason: form.reason,
        reflection: form.reflection,
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      };
      if (isManual) {
        Object.assign(payload, {
          entry: toNum(form.entry),
          exit: toNum(form.exit),
          size: toNum(form.size),
          fees: toNum(form.fees),
          leverage: toNum(form.leverage),
          opened_at: form.opened_at ? new Date(form.opened_at).toISOString() : null,
          closed_at: form.closed_at ? new Date(form.closed_at).toISOString() : null,
        });
      }
      const res = await axios.patch(`${API}/api/trades/${trade.id}`, payload, { withCredentials: true });
      toast.success("Trade aktualisiert");
      setEditing(false);
      onUpdated?.(res.data);
    } catch (e) {
      toast.error("Speichern fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/trades/${trade.id}`, { withCredentials: true });
      toast.success("Trade gelöscht"); onDeleted?.(); onOpenChange(false);
    } catch (e) { toast.error("Löschen fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setDeleting(false); setConfirmDelete(false); }
  };

  const download = (a) => {
    const link = document.createElement("a");
    link.href = a.data_url; link.download = a.name || "anhang"; link.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="trade-detail-dialog">
        <div className="p-6 md:p-8 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <DialogTitle className="font-heading text-2xl flex items-center gap-2 flex-wrap">
                  {trade.asset || "—"}
                  <Badge variant="secondary" className="rounded-full capitalize">{trade.side}</Badge>
                  <ProvenanceBadge source={SOURCE_LABEL[trade.provenance] || trade.provenance} />
                </DialogTitle>
                <DialogDescription className="text-muted-foreground mt-1">
                  {trade.market || "—"} · Trade-ID <span className="font-mono">{trade.id}</span>
                </DialogDescription>
              </div>
              <div className={`text-right ${plColor}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Netto-P/L</div>
                <div className="font-mono text-2xl mt-1" data-testid="trade-detail-pl">
                  {pl != null ? fmtEUR(pl, { signed: true }) : "—"}
                </div>
                {trade.fees != null && trade.fees > 0 && pl != null && (
                  <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                    Brutto {fmtEUR(pl + trade.fees, { signed: true })} − Gebühren {fmtNum(trade.fees, 2)}&nbsp;€
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>

          <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="trade-detail-facts">
            <Fact label="Entry" value={trade.entry != null ? fmtNum(trade.entry, 4) : "—"} />
            <Fact label="Exit" value={trade.exit != null ? fmtNum(trade.exit, 4) : "—"} />
            <Fact label="Größe" value={trade.size != null ? fmtNum(trade.size, 4) : "—"} />
            <Fact label="Hebel" value={trade.leverage && trade.leverage > 1 ? `${fmtNum(trade.leverage, 1)}×` : "—"} />
            <Fact label="Gebühren" value={trade.fees ? `${fmtNum(trade.fees, 2)} €` : "—"} />
            <Fact label="Stop" value={trade.stop != null ? fmtNum(trade.stop, 4) : "—"} />
            <Fact label="Risiko" value={trade.risk_pct != null ? `${fmtNum(trade.risk_pct, 2)} R` : "—"} />
            <Fact label="Eröffnet" value={trade.opened_at ? fmtDateTime(trade.opened_at) : "—"} />
            <Fact label="Geschlossen" value={trade.closed_at ? fmtDateTime(trade.closed_at) : "—"} />
          </section>

          {(trade.strategy_name || trade.no_strategy_reason || trade.reason || trade.reflection || trade.mood_before || trade.mood_after || editing) && (
            <section className="mt-6 space-y-3" data-testid="trade-detail-journal">
              {editing ? (
                <div className="space-y-3">
                  {isBroker && (
                    <div className="rounded-lg bg-[hsl(43_74%_49%/0.08)] border border-[hsl(43_74%_66%/0.3)] px-3 py-2 text-xs text-[hsl(43_74%_66%)]" data-testid="edit-broker-hint">
                      Zahlen (Entry, Exit, Größe, Fees, Datum) kommen von der Börse und sind gesperrt. Editierbar sind nur Journal-Metadaten.
                    </div>
                  )}
                  {isManual && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <FieldE label="Entry"><Input value={form.entry} onChange={(e) => set("entry", e.target.value)} data-testid="edit-entry" inputMode="decimal" className="font-mono" /></FieldE>
                      <FieldE label="Exit"><Input value={form.exit} onChange={(e) => set("exit", e.target.value)} data-testid="edit-exit" inputMode="decimal" className="font-mono" /></FieldE>
                      <FieldE label="Größe"><Input value={form.size} onChange={(e) => set("size", e.target.value)} data-testid="edit-size" inputMode="decimal" className="font-mono" /></FieldE>
                      <FieldE label="Gebühren €"><Input value={form.fees} onChange={(e) => set("fees", e.target.value)} data-testid="edit-fees" inputMode="decimal" className="font-mono" /></FieldE>
                      <FieldE label="Hebel ×"><Input value={form.leverage} onChange={(e) => set("leverage", e.target.value)} data-testid="edit-leverage" inputMode="decimal" className="font-mono" /></FieldE>
                      <FieldE label="Eröffnet"><Input type="datetime-local" value={form.opened_at} onChange={(e) => set("opened_at", e.target.value)} data-testid="edit-opened" className="font-mono text-xs" /></FieldE>
                      <FieldE label="Geschlossen"><Input type="datetime-local" value={form.closed_at} onChange={(e) => set("closed_at", e.target.value)} data-testid="edit-closed" className="font-mono text-xs" /></FieldE>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <FieldE label="Strategie"><Input value={form.strategy_name} onChange={(e) => set("strategy_name", e.target.value)} placeholder="z. B. Ausbruch nach Konsolidierung" data-testid="edit-strategy" /></FieldE>
                    <FieldE label="Stop"><Input value={form.stop} onChange={(e) => set("stop", e.target.value)} placeholder="Preis" data-testid="edit-stop" inputMode="decimal" className="font-mono" /></FieldE>
                    <FieldE label="Risiko (R)"><Input value={form.risk_pct} onChange={(e) => set("risk_pct", e.target.value)} placeholder="z. B. 1.0" data-testid="edit-risk" inputMode="decimal" className="font-mono" /></FieldE>
                    <FieldE label="Tags (kommagetrennt)"><Input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="Textbook, News, …" data-testid="edit-tags" /></FieldE>
                    <FieldE label="Stimmung vorher"><Input value={form.mood_before} onChange={(e) => set("mood_before", e.target.value)} placeholder="z. B. fokussiert" data-testid="edit-mood-before" /></FieldE>
                    <FieldE label="Stimmung nachher"><Input value={form.mood_after} onChange={(e) => set("mood_after", e.target.value)} placeholder="z. B. zufrieden" data-testid="edit-mood-after" /></FieldE>
                  </div>
                  <FieldE label="Setup-Notiz"><Textarea rows={2} value={form.reason} onChange={(e) => set("reason", e.target.value)} data-testid="edit-reason" /></FieldE>
                  <FieldE label="Reflexion / Lernen"><Textarea rows={3} value={form.reflection} onChange={(e) => set("reflection", e.target.value)} data-testid="edit-reflection" /></FieldE>
                </div>
              ) : (
                <>
                  {trade.strategy_name && <Block label="Strategie" value={trade.strategy_name} />}
                  {trade.no_strategy_reason && <Block label="Grund ohne Strategie" value={trade.no_strategy_reason} />}
                  {trade.reason && <Block label="Setup-Notiz" value={trade.reason} />}
                  {trade.reflection && <Block label="Journal / Reflexion" value={trade.reflection} />}
                  {(trade.mood_before || trade.mood_after) && (
                    <div className="grid grid-cols-2 gap-3">
                      {trade.mood_before && <Fact label="Stimmung vorher" value={trade.mood_before} />}
                      {trade.mood_after && <Fact label="Stimmung nachher" value={trade.mood_after} />}
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {trade.tags?.length > 0 && (
            <section className="mt-6" data-testid="trade-detail-tags">
              <div className="overline mb-2">Tags</div>
              <div className="flex flex-wrap gap-2">
                {trade.tags.map((t) => (
                  <Badge key={t} variant="secondary" className="rounded-full">{t}</Badge>
                ))}
              </div>
            </section>
          )}

          {/* Anhänge-Galerie */}
          <section className="mt-8" data-testid="trade-detail-attachments">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Anhänge {attachments.length > 0 && `· ${attachments.length}`}</div>
            </div>
            {attachments.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="trade-detail-attachments-empty">
                Keine Anhänge für diesen Trade.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {attachments.map((a) => {
                  const isImage = a.type?.startsWith("image/");
                  const kb = a.size < 1024 * 1024 ? `${(a.size / 1024).toFixed(0)} KB` : `${(a.size / 1024 / 1024).toFixed(1)} MB`;
                  return (
                    <div
                      key={a.id || a.name}
                      className="group rounded-xl border border-border bg-secondary/30 overflow-hidden hover:border-primary/50 transition-colors"
                      data-testid={`trade-detail-attach-${a.id || a.name}`}
                    >
                      <button
                        type="button"
                        onClick={() => isImage && setPreview(a)}
                        className="block w-full aspect-square bg-background/50 grid place-items-center overflow-hidden"
                        disabled={!isImage}
                      >
                        {isImage ? (
                          <img src={a.data_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        ) : a.type === "application/pdf" ? (
                          <FileText className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
                        ) : (
                          <FileImage className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
                        )}
                      </button>
                      <div className="p-2.5 flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs truncate" title={a.name}>{a.name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{kb}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => download(a)}
                          className="h-7 w-7 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0"
                          aria-label={`„${a.name}" herunterladen`}
                          data-testid={`trade-detail-download-${a.id || a.name}`}
                        >
                          <Download className="h-3.5 w-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Footer: Bearbeiten / Löschen */}
        <div className="border-t border-border px-6 md:px-8 py-4 bg-secondary/20 flex items-center justify-between gap-3 flex-wrap" data-testid="trade-detail-footer">
          {hasNoDates && isManual && !editing && (
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-[hsl(43_74%_66%)]" strokeWidth={1.5} />
              Kein Datum — vermutlich geplanter Trade
            </div>
          )}
          {!editing ? (
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setEditing(true)} data-testid="trade-detail-edit">
                <Pencil className="mr-2 h-4 w-4" strokeWidth={1.5} /> Bearbeiten
              </Button>
              {deletable && !confirmDelete && (
                <Button variant="ghost" size="sm" className="text-[hsl(0_66%_72%)] hover:text-[hsl(0_66%_72%)] hover:bg-[hsl(0_66%_50%/0.1)] rounded-full" onClick={() => setConfirmDelete(true)} data-testid="trade-detail-delete">
                  <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} /> Löschen
                </Button>
              )}
              {deletable && confirmDelete && (
                <>
                  <span className="text-sm">Wirklich löschen?</span>
                  <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setConfirmDelete(false)} disabled={deleting} data-testid="trade-detail-delete-cancel">Abbrechen</Button>
                  <Button size="sm" className="rounded-full bg-[hsl(0_66%_50%)] hover:bg-[hsl(0_66%_44%)]" onClick={doDelete} disabled={deleting} data-testid="trade-detail-delete-confirm">
                    {deleting ? "Wird gelöscht…" : "Ja, löschen"}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setEditing(false)} disabled={saving} data-testid="trade-detail-edit-cancel">Abbrechen</Button>
              <Button size="sm" className="rounded-full" onClick={doSave} disabled={saving} data-testid="trade-detail-edit-save">
                <Save className="mr-2 h-4 w-4" strokeWidth={1.5} /> {saving ? "Speichert…" : "Änderungen speichern"}
              </Button>
            </div>
          )}
        </div>

        {/* Lightbox */}
        {preview && (
          <div
            className="fixed inset-0 z-[60] bg-background/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setPreview(null)}
            data-testid="trade-detail-lightbox"
          >
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute top-6 right-6 h-10 w-10 rounded-full grid place-items-center bg-secondary hover:bg-secondary/80"
              aria-label="Vorschau schließen"
              data-testid="trade-detail-lightbox-close"
            >
              <XIcon className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <img
              src={preview.data_url}
              alt={preview.name}
              className="max-h-full max-w-full rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-sm break-all">{value}</div>
    </div>
  );
}

function Block({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function FieldE({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
